import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, Activity, Beaker, History, ChevronRight, LogOut, User, Crown, CreditCard, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import LanguageSelector from "@/components/LanguageSelector";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import SubscriptionLimitModal from "@/components/SubscriptionLimitModal";
import { logger } from '@/lib/logger';
import { InstrumentParamsFields, hasInstrumentFields } from "@/components/InstrumentParams";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Specialized analysis types - Spectroscopy
const SPECTROSCOPY_TYPES = [
  { value: "ftir-spectrum", label: "FTIR Spectrum", icon: Activity },
];

// Specialized analysis types - Chromatography
const CHROMATOGRAPHY_TYPES = [
  { value: "hplc-chromatogram", label: "HPLC Chromatogram", icon: FlaskConical },
];

const ALL_ANALYSIS_TYPES = [...SPECTROSCOPY_TYPES, ...CHROMATOGRAPHY_TYPES];

const HomePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisType, setAnalysisType] = useState("");
  const [instrumentParams, setInstrumentParams] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      toast.success(`File "${acceptedFiles[0].name}" selected`);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    multiple: false,
  });

  const handleAnalyze = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    if (!analysisType) {
      toast.error("Please select an analysis type");
      return;
    }

    try {
      setIsUploading(true);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      const fileId = uploadResponse.data.file_id;
      toast.success("File uploaded successfully");

      setIsUploading(false);
      setIsAnalyzing(true);

      const analysisResponse = await axios.post(
        `${API}/analyze`,
        {
          file_id: fileId,
          analysis_type: analysisType,
          instrument_params: hasInstrumentFields(analysisType) ? instrumentParams : null,
        },
        { withCredentials: true }
      );

      toast.success("Analysis completed!");
      navigate(`/analysis/${analysisResponse.data.id}`);
    } catch (error) {
      logger.error(error);
      
      // Check if it's a subscription limit error
      // Backend returns: { detail: { message: "...", subscription: {...} } }
      const errorDetail = error.response?.data?.detail;
      
      if (error.response?.status === 403 && typeof errorDetail === 'object' && errorDetail?.subscription) {
        setSubscriptionInfo(errorDetail.subscription);
        setShowLimitModal(true);
      } else {
        // Handle both string and object detail
        const errorMessage = typeof errorDetail === 'object' 
          ? (errorDetail?.message || "Analysis failed")
          : (errorDetail || "Analysis failed");
        toast.error(errorMessage);
      }
      
      setIsUploading(false);
      setIsAnalyzing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Email Verification Banner */}
      <EmailVerificationBanner user={user} />
      
      {/* Header */}
      <header className="glass-card border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Single row layout on all screens */}
          <div className="flex items-center justify-between gap-2">
            {/* Logo - always on left */}
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
              onClick={() => navigate('/')}
              data-testid="logo-home-link"
            >
              <img 
                src="/tabedlab-logo.png" 
                alt="TabedLab Logo" 
                className="w-8 h-8 sm:w-12 sm:h-12 object-contain"
              />
              <h1 className="text-lg sm:text-2xl font-bold text-slate-800 hidden xs:block">TabedLab</h1>
            </div>
            
            {/* Controls - right side */}
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSelector />
              
              <Button
                data-testid="history-button"
                variant="outline"
                onClick={() => navigate('/history')}
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-base px-2 sm:px-4 h-8 sm:h-10"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">History</span>
              </Button>

              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-button">
                  <Avatar>
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/subscription')} data-testid="subscription-menu-button">
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>Subscription</span>
                </DropdownMenuItem>
                {user?.is_admin && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/admin/control-panel')} data-testid="control-panel-menu-button">
                      <Crown className="mr-2 h-4 w-4 text-amber-500" />
                      <span>Control Panel</span>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12 fade-in">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Intelligent Laboratory
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
              Data Analysis
            </span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Upload your laboratory results and get AI-powered interpretations with ready-to-use reports
          </p>
        </div>

        {/* Main Upload Section */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="glass-card rounded-2xl p-8 fade-in">
            {/* File Upload */}
            <div
              {...getRootProps()}
              data-testid="file-dropzone"
              className={`dropzone mb-6 ${isDragActive ? 'active' : ''}`}
            >
              <input {...getInputProps()} />
              <div className="text-center">
                <Upload className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                {selectedFile ? (
                  <div>
                    <p className="text-lg font-semibold text-slate-800 mb-2">
                      {selectedFile.name}
                    </p>
                    <p className="text-sm text-slate-500">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-lg font-semibold text-slate-800 mb-2">
                      {isDragActive ? 'Drop file here' : 'Drag & drop your file here'}
                    </p>
                    <p className="text-sm text-slate-500">
                      or click to browse (CSV, Excel, TXT, PDF, PNG, JPG)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Analysis Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Select Analysis Type
              </label>
              <Select value={analysisType} onValueChange={setAnalysisType}>
                <SelectTrigger data-testid="analysis-type-select" className="w-full">
                  <SelectValue placeholder="Choose analysis type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="text-cyan-600 font-semibold">Spectroscopy</SelectLabel>
                    {SPECTROSCOPY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="text-purple-600 font-semibold">Chromatography</SelectLabel>
                    {CHROMATOGRAPHY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {hasInstrumentFields(analysisType) && (

              <div className="mb-6">

                <InstrumentParamsFields analysisType={analysisType} value={instrumentParams} onChange={setInstrumentParams} />

              </div>

            )}


            {/* Analyze Button */}
            <Button
              data-testid="analyze-button"
              onClick={handleAnalyze}
              disabled={!selectedFile || !analysisType || isUploading || isAnalyzing}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-6 rounded-xl text-lg btn-hover"
            >
              {isUploading ? (
                <span>Uploading...</span>
              ) : isAnalyzing ? (
                <span>Analyzing...</span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Activity className="w-5 h-5" />
                  Analyze Data
                  <ChevronRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <div className="glass-card rounded-xl p-6 hover:shadow-xl transition-all">
            <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Multiple Formats</h3>
            <p className="text-sm text-slate-600">
              Support for CSV, Excel, PDF, images, and text files
            </p>
          </div>

          <div className="glass-card rounded-xl p-6 hover:shadow-xl transition-all">
            <div className="bg-cyan-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Activity className="w-6 h-6 text-cyan-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">AI-Powered Analysis</h3>
            <p className="text-sm text-slate-600">
              Advanced AI interprets your data and provides insights
            </p>
          </div>

          <div className="glass-card rounded-xl p-6 hover:shadow-xl transition-all">
            <div className="bg-indigo-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Beaker className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Export Reports</h3>
            <p className="text-sm text-slate-600">
              Download professional PDF reports instantly
            </p>
          </div>
        </div>
      </div>

      {/* Subscription Limit Modal */}
      <SubscriptionLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        subscription={subscriptionInfo}
      />
    </div>
  );
};

export default HomePage;